<script lang="ts" setup>
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { PropType, ref, watch } from 'vue'

import { trackEvent, TrackEventData } from '@/directives/track'
import { SizeOptions } from '@/types'

import AppIcon from './AppIcon.vue'

type Option = { label: string, value: number | string }

const props = defineProps({
  disabled: { type: Boolean, default: false },
  direction: { type: String as PropType<'top' | 'bottom'>, default: 'bottom' },
  id: { type: String, required: true },
  options: { type: Array as PropType<Option[]>, required: true },
  size: { type: String as PropType<SizeOptions>, default: 'sm' },
  trackingData: { type: Object as PropType<TrackEventData>, default: () => ({} as TrackEventData) }
})

const el = ref<HTMLElement>()

const selected = ref(props.options[0])
const open = ref(false)
const { escape } = useMagicKeys()
const { activate, deactivate } = useFocusTrap(el, {
  initialFocus: false,
  allowOutsideClick: true,
  returnFocusOnDeactivate: false
})

const hide = () => { open.value = false }
const show = () => { open.value = true }
const toggle = () => { open.value ? hide() : show() }

watch([() => props.disabled, escape, selected], hide)
watch(open, (value) => { value ? activate() : deactivate() })
watch(selected, (_selected) => trackEvent({
  ...props.trackingData,
  eventAction: _selected.value as string
}))

onClickOutside(el, hide)
</script>

<template>
  <div ref="el" :class="[{ open }, size, direction]" class="app-combobox relative">
    <button
      :id="`combo-${id}`"
      :aria-expanded="open"
      :aria-controls="`listbox-${id}`"
      :aria-labelledby="`label-${id}`"
      :disabled="disabled"
      :title="$t('accessibility.combobox', { msg: id })"
      aria-haspopup="listbox"
      class="selected flex items-center gap-1"
      role="combobox"
      @click="toggle"
    >
      <span class="uppercase"><slot name="selected" v-bind="selected" /></span>
      <AppIcon
        icon="arrow"
        :class="{ 'scale--100': open }"
        class="shrink-0 transform transition-transform-300"
      />
    </button>
    <Transition>
      <ul
        v-if="open"
        :id="`listbox-${id}`"
        :aria-labelledby="`label-${id}`"
        class="options flex flex-col absolute left-0 right-0"
        role="listbox"
        tabindex="-1"
      >
        <template v-for="option in options" :key="option.value">
          <li
            v-if="option.value !== selected.value"
            :id="`option-${option.value}`"
            :aria-disabled="disabled"
            :aria-selected="option.value === selected.value"
            role="option"
            @click="selected = option"
          >
            <slot name="option" v-bind="option" />
          </li>
        </template>
      </ul>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.app-combobox {
  font-weight: 400;
  line-height: 110%;

  &.xs {
    font-size: rem(16px);
  }

  &.sm {
    font-size: rem(18px);
  }

  &.md {
    font-size: rem(24px);
  }

  &.lg {
    font-size: rem(34px);
  }
}

.selected {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.options {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;

  .bottom & {
    top: 100%;
  }

  .top & {
    bottom: 100%;
  }

  li {
    margin: rem(4px 0);

    ::v-deep(*) {
      display: inline-block;
      font-size: inherit;
    }
  }
}
</style>
